/**
 * PATH: apps/hr/src/pages/documents/document-detail/DetailBadges.tsx
 * PURPOSE: StatusBadge, ConfidenceBadge, and TagBadge for document detail view
 */

import { Tag, X } from 'lucide-react'
import { cn } from '../../../lib/utils'
import type { DocumentStatus } from '../../../types'

export function StatusBadge({ status }: { status: DocumentStatus }) {
  const styles: Record<DocumentStatus, { bg: string; text: string }> = {
    pending_ocr: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400' },
    needs_review: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400' },
    approved: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400' },
    rejected: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400' },
    expired: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400' },
  }
  const labels: Record<DocumentStatus, string> = {
    pending_ocr: 'Processing', needs_review: 'Needs Review', approved: 'Approved', rejected: 'Rejected', expired: 'Expired',
  }
  return (
    <span className={cn('px-2.5 py-1 rounded-full text-xs font-medium', styles[status].bg, styles[status].text)}>
      {labels[status]}
    </span>
  )
}

export function ConfidenceBadge({ confidence }: { confidence: number }) {
  const color = confidence >= 90 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                confidence >= 70 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
  return <span className={cn('px-2.5 py-1 rounded-full text-xs font-medium', color)}>{confidence}%</span>
}

export function TagBadge({ tag, removable, onRemove }: { tag: string; removable?: boolean; onRemove?: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-100 dark:bg-slate-800 rounded-full text-xs font-medium text-slate-600 dark:text-slate-400">
      <Tag className="w-3 h-3" />
      {tag}
      {removable && onRemove && (
        <button onClick={(e) => { e.stopPropagation(); onRemove() }} className="ml-1 hover:text-red-500 transition-colors">
          <X className="w-3 h-3" />
        </button>
      )}
    </span>
  )
}

export function getConfidenceColorClass(confidence: number) {
  if (confidence >= 90) return 'text-emerald-600 dark:text-emerald-400'
  if (confidence >= 70) return 'text-amber-600 dark:text-amber-400'
  return 'text-red-600 dark:text-red-400'
}
